// src/pages/Invoices/NewInvoice.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1000px', margin: '30px auto', color: '#1f2937' },

    header: { marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    title: { fontSize: '28px', fontWeight: '800', color: '#111827', margin: 0, letterSpacing: '-0.5px' },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    backButton: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' },

    box: { background: 'white', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: '1px solid #e5e7eb', marginBottom: '24px' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' },

    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none', transition: 'all 0.2s', height: '46px', backgroundColor: '#f9fafb' },
    select: { padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none', backgroundColor: '#f9fafb', height: '46px', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: '16px' },

    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' },
    sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 },

    productInputGrid: { display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: '16px', alignItems: 'end', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' },

    button: { padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '46px' },
    addButton: { background: '#3b82f6', color: 'white', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)' },
    saveButton: { background: '#10b981', color: 'white', fontSize: '16px', padding: '14px 40px', boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)' },

    tableContainer: { border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '32px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#f3f4f6', padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: '16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#1f2937', verticalAlign: 'middle' },
    tr: { transition: 'background-color 0.1s' },

    totalsWrapper: { display: 'flex', justifyContent: 'flex-end', marginTop: '24px' },
    totalsBox: { width: '320px', padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' },
    totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#4b5563' },
    totalLabel: { fontWeight: '500' },
    grandTotalRow: { display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #e5e7eb' },
    grandTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#111827' },
    grandTotalValue: { fontSize: '20px', fontWeight: '800', color: '#059669' },
    usdInfo: { marginTop: '8px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic', textAlign: 'right' },

    deleteBtn: { background: 'white', border: '1px solid #fee2e2', color: '#ef4444', cursor: 'pointer', padding: '8px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', width: '32px', height: '32px' },
    emptyState: { padding: '48px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', background: '#fafafa', borderRadius: '8px' }
};

function NewInvoice() {
    const { currentUser, userData, canViewPrices } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Form State
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(''); // Opsiyonel Vade Tarihi
    const [lineItems, setLineItems] = useState([]);
    const [vatRatePercent, setVatRatePercent] = useState(20); // Default VAT % (e.g. 20)
    const [usdRate, setUsdRate] = useState(null);

    // Line Item State
    const [currentItem, setCurrentItem] = useState({ productId: '', quantity: 1 });

    // companyId: personel kullanıcılar için userData.companyId, admin için currentUser.uid
    const companyId = userData?.companyId || currentUser?.uid;

    // Veri Çekme
    useEffect(() => {
        if (!companyId) return;
        const unsubCustomers = onSnapshot(query(collection(db, 'companies', companyId, 'customers')), snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        // Döviz Kuru Çekme (Frankfurter API - Ücretsiz ve anahtarsız)
        fetch('https://api.frankfurter.app/latest?from=USD&to=TRY')
            .then(res => res.json())
            .then(data => setUsdRate(data.rates.TRY))
            .catch(err => console.error("Döviz kuru alınamadı", err));

        return () => { unsubCustomers(); unsubProducts(); };
    }, [companyId]);

    // Keep a ref to the latest handleSaveInvoice to avoid stale closures
    const handleSaveInvoiceRef = React.useRef(handleSaveInvoice);
    React.useEffect(() => { handleSaveInvoiceRef.current = handleSaveInvoice; });

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSaveInvoiceRef.current();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Pre-populate form when data is loaded and we have shipment data
    useEffect(() => {
        if (!location.state || customers.length === 0 || products.length === 0) return;

        const { shipmentData, shipmentsByOrder } = location.state;

        if (shipmentData) {
            // Single shipment case - müşteri eşleştirme: önce customerId, sonra customerName/customer
            const customer = shipmentData.customerId 
                ? customers.find(c => c.id === shipmentData.customerId)
                : customers.find(c => c.name === (shipmentData.customerName || shipmentData.customer));
            if (customer) {
                setSelectedCustomer(customer.id);
            }

            // Convert shipment items to invoice line items
            const newLineItems = (shipmentData.items || []).map(item => {
                const product = products.find(p => p.name === item.name || p.id === item.productId);
                const itemPrice = Number(item.price) || (product ? Number(product.salePrice) : 0);
                return {
                    productId: item.productId || (product ? product.id : ''),
                    name: item.name,
                    quantity: Number(item.quantity),
                    price: itemPrice,
                    total: Number(item.quantity) * itemPrice
                };
            });

            setLineItems(newLineItems);
        } else if (shipmentsByOrder) {
            // Multiple shipments case - Consolidate items from ALL selected orders
            const orderIds = Object.keys(shipmentsByOrder);
            if (orderIds.length > 0) {
                // Determine selected customer from the first shipment found
                let firstCustomerFound = null;
                const allItems = [];

                orderIds.forEach(orderId => {
                    const shipments = shipmentsByOrder[orderId];
                    shipments.forEach(shipment => {
                        if (!firstCustomerFound) {
                            firstCustomerFound = shipmentsByOrder[orderId][0].customerId
                                ? customers.find(c => c.id === shipmentsByOrder[orderId][0].customerId)
                                : customers.find(c => c.name === (shipmentsByOrder[orderId][0].customerName || shipmentsByOrder[orderId][0].customer));
                        }
                        allItems.push(...(shipment.items || []));
                    });
                });

                if (firstCustomerFound) {
                    setSelectedCustomer(firstCustomerFound.id);
                }

                // Group items by name and sum quantities, preserve price from shipment
                const itemMap = {};
                allItems.forEach(item => {
                    const key = item.productId || item.name;
                    if (itemMap[key]) {
                        itemMap[key].quantity += Number(item.quantity);
                    } else {
                        itemMap[key] = {
                            productId: item.productId,
                            name: item.name,
                            quantity: Number(item.quantity),
                            price: Number(item.price) || 0
                        };
                    }
                });

                // Convert to invoice line items using shipment prices
                const newLineItems = Object.values(itemMap).map(item => {
                    const product = products.find(p => p.id === item.productId || p.name === item.name);
                    const itemPrice = item.price || (product ? Number(product.salePrice) : 0);
                    return {
                        productId: item.productId || (product ? product.id : ''),
                        name: item.name,
                        quantity: item.quantity,
                        price: itemPrice,
                        total: item.quantity * itemPrice
                    };
                });

                setLineItems(newLineItems);
            }
        }
    }, [location.state, customers, products]);

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) return;
        const product = products.find(p => p.id === currentItem.productId);
        if (!product) return;

        setLineItems(prev => [...prev, {
            productId: product.id,
            name: product.name,
            quantity: Number(currentItem.quantity),
            price: Number(product.salePrice),
            total: Number(currentItem.quantity) * Number(product.salePrice)
        }]);
        setCurrentItem({ productId: '', quantity: 1 }); // Formu sıfırla
    };

    const handleRemoveItem = (index) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    // Hesaplamalar
    const subTotal = lineItems.reduce((acc, item) => acc + item.total, 0);
    const vatRate = vatRatePercent / 100;
    const vatAmount = subTotal * vatRate;
    const grandTotal = subTotal + vatAmount;
    const grandTotalUsd = usdRate ? (grandTotal / usdRate).toFixed(2) : 0;

    const handleSaveInvoice = async () => {
        if (!selectedCustomer || lineItems.length === 0) {
            toast.error("Lütfen bir müşteri seçin ve en az bir ürün ekleyin.");
            return;
        }

        const customer = customers.find(c => c.id === selectedCustomer);
        const isFromShipment = !!(location.state?.shipmentData || location.state?.shipmentsByOrder);

        const batch = writeBatch(db);

        // 1. Faturayı kaydet
        const invoiceRef = doc(collection(db, 'companies', companyId, 'invoices'));
        const invoiceData = {
            customerId: customer.id,
            customerName: customer.name,
            invoiceDate: new Date(invoiceDate),
            lineItems: lineItems,
            vatRate: vatRate, // Saving as decimal (e.g. 0.20) for consistency
            subTotal: subTotal,
            vatAmount: vatAmount,
            total: grandTotal,
            currency: 'TRY', // Fatura TRY olarak oluşturuluyor
            status: 'Oluşturuldu',
            createdAt: serverTimestamp()
        };

        if (dueDate) {
            invoiceData.dueDate = new Date(dueDate);
        }

        batch.set(invoiceRef, invoiceData);

        // 2. Stokları güncelle - SADECE sevkiyattan gelmiyorsa (sevkiyatta stok zaten düşürülmüştür)
        if (!isFromShipment) {
            lineItems.forEach(item => {
                if (item.productId) {
                    const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                    const product = products.find(p => p.id === item.productId);
                    if (product) {
                        const newQuantity = (product.quantity || 0) - item.quantity;
                        batch.update(productRef, { quantity: newQuantity });
                    }
                }
            });
        }

        // 3. Müşteri bakiyesini güncelle
        const customerRef = doc(db, 'companies', companyId, 'customers', customer.id);
        const currentBalance = customer.balance || 0;

        // Fatura TRY olarak oluşturuluyor, müşterinin para birimine göre dönüşüm yap
        let balanceToSubtract = grandTotal;
        if (customer.currency === 'USD') {
            // Müşteri USD ise, TRY tutarı USD'ye çevir
            if (usdRate) {
                balanceToSubtract = grandTotal / usdRate;
            } else {
                // Kur bilgisi yoksa varsayılan değer kullan
                balanceToSubtract = grandTotal / 30;
            }
        }

        const newBalance = currentBalance - balanceToSubtract; // Müşteri borçlandı
        batch.update(customerRef, { balance: newBalance });

        // 3b. Bakiye geçmişi kaydı oluştur (mutabakat raporları için)
        const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
        batch.set(balanceHistoryRef, {
            date: serverTimestamp(),
            type: 'Fatura',
            description: `Fatura oluşturuldu`,
            amount: balanceToSubtract,
            previousBalance: currentBalance,
            newBalance: newBalance,
            invoiceId: invoiceRef.id
        });

        // 4. Update shipment status if this invoice was created from a shipment
        const { shipmentData, shipmentsByOrder } = location.state || {};

        if (shipmentData && shipmentData.id) {
            // Single shipment case - invoiceId geri yazılıyor
            const shipmentRef = doc(db, 'companies', companyId, 'orders', shipmentData.orderId, 'shipments', shipmentData.id);
            batch.update(shipmentRef, {
                status: 'Faturalandı',
                invoiceId: invoiceRef.id,
                invoicedAt: new Date()
            });
        } else if (shipmentsByOrder) {
            // Multiple shipments case
            for (const [orderId, shipments] of Object.entries(shipmentsByOrder)) {
                for (const shipment of shipments) {
                    const shipmentRef = doc(db, 'companies', companyId, 'orders', orderId, 'shipments', shipment.id);
                    batch.update(shipmentRef, {
                        status: 'Faturalandı',
                        invoiceId: invoiceRef.id,
                        invoicedAt: new Date()
                    });
                }
            }
        }

        try {
            await batch.commit();

            // Navigate back to the shipments page if this was created from a shipment
            if (location.state && (location.state.shipmentData || location.state.shipmentsByOrder)) {
                // Show success message and navigate back to uninvoiced shipments
                toast.success('Fatura başarıyla oluşturuldu!');
                navigate('/siparisler/faturalastirilmamis-sevkiyatlar');
            } else {
                toast.success('Fatura başarıyla oluşturuldu!');
                navigate('/faturalar');
            }
        } catch (error) {
            console.error("Fatura kaydedilirken hata:", error);
            toast.error("Fatura kaydedilirken bir hata oluştu.");
        }
    };


    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Yeni Fatura Oluştur</h1>
                    <p style={styles.subtitle}>Fatura detaylarını girerek yeni bir fatura oluşturun.</p>
                </div>
                <button onClick={() => navigate('/faturalar')} style={styles.backButton}>
                    ← Faturalara Dön
                </button>
            </div>

            <div style={styles.box}>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri</label>
                        <select style={styles.select} value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} required>
                            <option value="">Bir Müşteri Seçin...</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Fatura Tarihi</label>
                        <input style={styles.input} type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Vade Tarihi (Opsiyonel)</label>
                        <input style={styles.input} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>KDV Oranı (%)</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="number"
                                style={{ ...styles.input, paddingRight: '24px' }}
                                value={vatRatePercent}
                                onChange={e => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 100) {
                                        setVatRatePercent(val);
                                    } else if (e.target.value === '') {
                                        setVatRatePercent('');
                                    }
                                }}
                                placeholder="Örn: 20"
                            />
                            <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>%</span>
                        </div>
                    </div>
                </div>

                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Ürünler ve Hizmetler</h3>
                </div>

                {/* Ürün Ekleme */}
                <div style={styles.productInputGrid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün / Hizmet</label>
                        <select style={styles.select} value={currentItem.productId} onChange={e => setCurrentItem(prev => ({ ...prev, productId: e.target.value }))}>
                            <option value="">Bir Ürün Seçin...</option>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name} - {canViewPrices ? `${p.salePrice} ₺` : '**'}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input style={styles.input} type="number" min="1" value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({ ...prev, quantity: e.target.value }))} />
                    </div>
                    {/* Placeholder for Price Display if needed */}
                    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '12px' }}>
                        {/* Optional: Show selected product price */}
                    </div>
                    <button onClick={handleAddItem} style={{ ...styles.button, ...styles.addButton }}>
                        <span style={{ fontSize: '18px' }}>+</span> Ekle
                    </button>
                </div>

                {/* Ürün Listesi Tablosu */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th} width="120">Miktar</th>
                                <th style={styles.th} width="150" align="right">Birim Fiyat</th>
                                <th style={styles.th} width="150" align="right">Toplam</th>
                                <th style={styles.th} width="60"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={styles.emptyState}>
                                        Henüz ürün eklenmedi. Yukarıdaki formdan ürün ekleyebilirsiniz.
                                    </td>
                                </tr>
                            ) : (
                                lineItems.map((item, index) => (
                                    <tr key={index} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '500' }}>{item.name}</div>
                                        </td>
                                        <td style={styles.td}>{item.quantity}</td>
                                        <td style={styles.td} align="right">{canViewPrices ? `${item.price.toFixed(2)} ₺` : '***'}</td>
                                        <td style={{ ...styles.td, fontWeight: '600' }} align="right">{canViewPrices ? `${item.total.toFixed(2)} ₺` : '***'}</td>
                                        <td style={styles.td} align="center">
                                            <button onClick={() => handleRemoveItem(index)} style={styles.deleteBtn} title="Sil">
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.totalsWrapper}>
                    <div style={styles.totalsBox}>
                        <div style={styles.totalRow}>
                            <span style={styles.totalLabel}>Ara Toplam:</span>
                            <span>{canViewPrices ? `${subTotal.toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={styles.totalRow}>
                            <span style={styles.totalLabel}>KDV (%{vatRatePercent || 0}):</span>
                            <span>{canViewPrices ? `${vatAmount.toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={styles.grandTotalRow}>
                            <span style={styles.grandTotalLabel}>GENEL TOPLAM:</span>
                            <span style={styles.grandTotalValue}>{canViewPrices ? `${grandTotal.toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={styles.usdInfo}>
                            Yaklaşık Dolar Karşılığı: {canViewPrices ? `$${grandTotalUsd}` : '***'}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button onClick={handleSaveInvoice} style={{ ...styles.button, ...styles.saveButton }}>
                        Faturayı Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NewInvoice;
